import {
  Word,
  Animation,
  StyleConfig,
  Background,
} from '@contentlab/types/captions';
import {WordBox, WordInfo} from './shared';

// Smooth animation specific interface
export interface HighlightSmoothTextRenderParams {
  context: CanvasRenderingContext2D;
  word: Word;
  x: number;
  y: number;
  fontColor: string;
  strokeColor: string;
  strokeWidth: number;
  style: StyleConfig;
  animation: Animation;
  currentTime: number;
}

export function smoothProgress(progress: number): number {
  const clippedProgress = Math.max(0, Math.min(1, progress));

  if (clippedProgress < 0.5) {
    return 4 * clippedProgress * clippedProgress * clippedProgress;
  }
  const p = 2 * clippedProgress - 2;
  return 1 + (p * p * p) / 2;
}

export function interpolateBox(
  start: {width: number; x: number; y: number},
  end: {width: number; x: number; y: number},
  progress: number
): {width: number; x: number; y: number} {
  const smoothedProgress = smoothProgress(progress);
  return {
    width: start.width + (end.width - start.width) * smoothedProgress,
    x: start.x + (end.x - start.x) * smoothedProgress,
    y: start.y + (end.y - start.y) * smoothedProgress,
  };
}

// Animation progress calculation
export function calculateHighlightSmoothTransitionProgress(
  currentWord: Word,
  currentTime: number,
  transitionPercentage = 0.2
): number {
  const wordDuration = currentWord.end - currentWord.start;
  const transitionDuration = wordDuration * transitionPercentage;
  const elapsedInWord = currentTime - currentWord.start;
  return Math.min(1, elapsedInWord / transitionDuration);
}

// Background rendering helper
export function drawInterpolatedBackground(
  context: CanvasRenderingContext2D,
  currentWordInfo: WordInfo | null,
  previousWordInfo: WordInfo | null,
  wordBoxes: WordBox[],
  progress: number,
  animation: Animation,
  style: StyleConfig,
  fontSize: number,
  position: {x: number; lineHeight: number; startY: number},
  interpolateBoxFn: (
    start: {x: number; width: number; y: number},
    end: {x: number; width: number; y: number},
    progress: number
  ) => {x: number; width: number; y: number},
  drawBackground: (
    context: CanvasRenderingContext2D,
    background: Background,
    lines: string[],
    fontSize: number,
    position: {x: number; lineHeight: number; startY: number},
    width?: number
  ) => void
): void {
  let startBox: {x: number; width: number; y: number};
  let endBox: {x: number; width: number; y: number};

  // Handle case where there's no current word but there is a previous word
  if (
    !currentWordInfo &&
    previousWordInfo &&
    wordBoxes[previousWordInfo.index]
  ) {
    const prevBox = wordBoxes[previousWordInfo.index];
    // Keep the box at the previous word position
    startBox = {x: prevBox.x, width: prevBox.width, y: prevBox.y};
    endBox = {x: prevBox.x, width: prevBox.width, y: prevBox.y};
  } else if (
    currentWordInfo &&
    previousWordInfo &&
    wordBoxes[previousWordInfo.index] &&
    wordBoxes[currentWordInfo.index]
  ) {
    const prevBox = wordBoxes[previousWordInfo.index];
    const currBox = wordBoxes[currentWordInfo.index];
    startBox = {x: prevBox.x, width: prevBox.width, y: prevBox.y};
    endBox = {x: currBox.x, width: currBox.width, y: currBox.y};
  } else if (currentWordInfo && wordBoxes[currentWordInfo.index]) {
    // First word - start from zero width to avoid the small box at center
    const currBox = wordBoxes[currentWordInfo.index];
    startBox = {x: currBox.x, width: 0, y: currBox.y};
    endBox = {x: currBox.x, width: currBox.width, y: currBox.y};
  } else {
    return;
  }

  const interpolatedBox = interpolateBoxFn(startBox, endBox, progress);
  if (animation.background) {
    drawBackground(
      context,
      animation.background,
      [''],
      fontSize,
      {
        x: interpolatedBox.x,
        lineHeight: position.lineHeight,
        startY: interpolatedBox.y,
      },
      interpolatedBox.width
    );
  }
}

// Word rendering helper
export function drawWordHighlightSmooth(
  params: HighlightSmoothTextRenderParams,
  getHighlightFillColor: (
    fontColor: string,
    currentTime: number,
    word: Word,
    animation: Animation
  ) => string,
  drawDropShadow: (
    context: CanvasRenderingContext2D,
    style: StyleConfig,
    fontColor: string,
    x: number,
    y: number,
    text: string,
    strokeWidth?: number,
    scaleFactor?: number
  ) => void,
  drawStroke: (
    context: CanvasRenderingContext2D,
    strokeColor: string,
    strokeWidth: number,
    text: string,
    x: number,
    y: number
  ) => void
): void {
  const {
    context,
    word,
    x,
    y,
    fontColor,
    strokeColor,
    strokeWidth,
    style,
    animation,
    currentTime,
  } = params;

  const fillColor = getHighlightFillColor(
    fontColor,
    currentTime,
    word,
    animation
  );

  if (style.styling.dropShadow) {
    drawDropShadow(context, style, fontColor, x, y, word.text, strokeWidth);
  }

  if (strokeWidth > 0 && strokeColor !== 'transparent') {
    drawStroke(context, strokeColor, strokeWidth, word.text, x, y);
  }

  context.fillStyle = fillColor;
  context.fillText(word.text, x, y);
}
